import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';
import { SIDEBAR_DESTINATION_IDS } from '~/components/sidebar-nav-catalog';

/**
 * FRONT-285 / FRONT-2381 — the sidebar's user-owned layout: the drag-to-reorder
 * order of its destination list. FRONT-2381 flattened the sidebar (no more
 * "Frequent" zone vs. a collapsible "More" zone — every visible destination
 * renders in one list), so this store now holds just that one ordering.
 * Persisted to a single localStorage key so drag-and-drop curation survives a
 * reload; hydration is defensive (corrupt JSON, a wrong version, or an id
 * that no longer exists in the catalog all fall back safely instead of
 * throwing).
 */

const STORAGE_KEY = 'eddy.sidebar.layout';
const LAYOUT_VERSION = 1;

/** Exported so tests (and any other consumer) can assert against the exact
 *  default set without duplicating the literal — see
 *  `sidebar-nav-catalog.test.ts`'s "every default-Frequent id exists in the
 *  catalog and is ungated" guard. Also what `resolveSidebarOrder` (mission-control-sidebar.tsx)
 *  leads a fresh browser's flat list with, exactly like the old Frequent zone
 *  did — the name stayed `DEFAULT_FREQ` rather than being renamed, so that
 *  guard (and this module's own default) don't need to change together. */
export const DEFAULT_FREQ: string[] = ['inbox', 'memory', 'files', 'agents'];

function sameOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

interface StoredLayout {
  v: number;
  freq: string[];
  /** FRONT-2381 removed the "More" zone this used to gate open/closed. Kept
   *  here, optional and unread, only so a payload written by an OLDER build
   *  still parses (the `v`/`freq` shape is unchanged) instead of being
   *  rejected outright for an extra field. */
  moreOpen?: boolean;
}

function isKnownId(id: unknown): id is string {
  return typeof id === 'string' && SIDEBAR_DESTINATION_IDS.includes(id);
}

function loadOrder(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [...DEFAULT_FREQ];
    const parsed = JSON.parse(raw) as Partial<StoredLayout> | null;
    if (!parsed || parsed.v !== LAYOUT_VERSION || !Array.isArray(parsed.freq)) {
      return [...DEFAULT_FREQ];
    }
    // Drop ids that no longer exist in the catalog, and de-dupe defensively —
    // a hand-edited or stale storage value shouldn't render an unknown row or
    // the same row twice.
    return Array.from(new Set(parsed.freq.filter(isKnownId)));
  } catch {
    return [...DEFAULT_FREQ];
  }
}

function persistOrder(order: string[]) {
  try {
    const payload: StoredLayout = { v: LAYOUT_VERSION, freq: order };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota or storage disabled — layout becomes session-only */
  }
}

export interface SidebarStore {
  order: string[];
  /** Replace the full stored order. No-op (no state change, no persist) when
   *  `ids` is identical to the current order — this is what makes a drop
   *  that resolves to no real change (the caller's `computeDropOrder` returns
   *  the same array for a self-drop) safe to call unconditionally, and keeps
   *  the FRONT-285 review fix (dropping a row onto itself must never move
   *  it, or persist) true for the flat list too. */
  setOrder: (ids: string[]) => void;
}

export function createSidebarStore(): StoreApi<SidebarStore> {
  const initial = loadOrder();
  return createStore<SidebarStore>('sidebar-store', (set, get) => ({
    order: initial,
    setOrder: (ids) => {
      const current = get().order;
      if (sameOrder(ids, current)) return;
      persistOrder(ids);
      set({ order: ids });
    },
  }));
}

let store: StoreApi<SidebarStore> | undefined;

function getStore(): StoreApi<SidebarStore> {
  if (!store) store = createSidebarStore();
  return store;
}

export function useSidebarStore<T>(selector: (s: SidebarStore) => T): T {
  return useStore(getStore(), selector);
}
